'use client';

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Sparkles, X } from 'lucide-react';
import { trackEvent } from '@/lib/posthog';
import { useLocalizedNavigate } from '@/components/i18n';
import { useTranslation } from 'react-i18next';

const BANNER_DISMISSED_KEY = 'new_pricing_banner_dismissed';
const MAX_DISMISSALS = 3;
const PRICING_CHANGE_DATE = '2026-03-01';

/**
 * Announcement banner for returning free users about the new $29/mo pricing.
 * Shows only for users who signed up before the pricing change date.
 * Dismissable up to 3 times, then permanently hidden.
 */
export function NewPricingBanner() {
  const { isFree, isLoading } = useSubscription();
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation('app');
  const [isDismissed, setIsDismissed] = useState(true); // Start hidden to prevent flash
  const hasTrackedShown = useRef(false);

  const isAdvisorRoute = location.pathname.startsWith('/advisor');

  const isPrePricingChangeUser = profile?.created_at
    ? new Date(profile.created_at) < new Date(PRICING_CHANGE_DATE)
    : false;

  useEffect(() => {
    const stored = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (stored) {
      try {
        const { count } = JSON.parse(stored);
        if (count >= MAX_DISMISSALS) {
          setIsDismissed(true);
          return;
        }
      } catch {
        // Invalid JSON, treat as not dismissed
      }
    }
    setIsDismissed(false);
  }, []);

  // Track shown event once per session
  useEffect(() => {
    if (!isDismissed && !isLoading && isFree && isPrePricingChangeUser && !hasTrackedShown.current) {
      trackEvent('new_pricing_banner_shown');
      hasTrackedShown.current = true;
    }
  }, [isDismissed, isLoading, isFree, isPrePricingChangeUser]);

  if (isLoading || !isFree || isAdvisorRoute || isDismissed || !isPrePricingChangeUser) {
    return null;
  }

  const handleDismiss = () => {
    const stored = localStorage.getItem(BANNER_DISMISSED_KEY);
    let count = 0;
    if (stored) {
      try {
        count = JSON.parse(stored).count || 0;
      } catch {
        // Reset on invalid JSON
      }
    }
    count++;
    localStorage.setItem(
      BANNER_DISMISSED_KEY,
      JSON.stringify({ count, lastDismissedAt: new Date().toISOString() })
    );
    trackEvent('new_pricing_banner_dismissed', { dismiss_count: count });
    setIsDismissed(true);
  };

  const handleCTAClick = () => {
    trackEvent('new_pricing_banner_clicked');
    navigate('/pricing');
  };

  return (
    <div className="bg-gradient-to-r from-amber-50 to-amber-100/80 border-b border-amber-200 px-4 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center h-6 w-6 rounded-full bg-amber-200/60 flex-shrink-0">
            <Sparkles className="h-3.5 w-3.5 text-amber-700" />
          </div>
          <p className="text-sm text-amber-900">
            <span className="font-semibold">{t('newPricingBanner.badge')}</span>{' '}
            <span className="hidden sm:inline">{t('newPricingBanner.message')}</span>
            <span className="sm:hidden">{t('newPricingBanner.messageShort', t('newPricingBanner.message'))}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
            onClick={handleCTAClick}
          >
            <span className="hidden sm:inline">{t('newPricingBanner.cta')}</span>
            <span className="sm:hidden">{t('newPricingBanner.ctaShort')}</span>
          </Button>
          <button
            onClick={handleDismiss}
            className="text-amber-500 hover:text-amber-700 p-1"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
